import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import { getSchedulingFrom } from '@src/common/seatMaps';
import { makeSelectedTariffs, type SelectedTariff } from '@src/domains/tariffs';
import {
  productSelector,
  selectedProductResources,
  selectedProductTariffHour,
  selectedTariffsSelector,
  setSelectedResources,
  type FullProduct,
  type SelectedResource,
  type TariffsPerType,
} from '@src/stores/product';
import type { Scheduling, ScheduleTime } from '@src/core/buyerApiClient';

export interface UseProductSeatmapSelectionResult {
  widgetId: string;
  productId: string | null;
  product: FullProduct | undefined;
  tariffsPerTypeSelected: TariffsPerType[];
  selectedHour: ScheduleTime | null;
  selectedTime: number | undefined;
  selectedTariffs: SelectedTariff[];
  scheduling: Scheduling | undefined;
  requiresSeatSelection: boolean;
  selectedResources: SelectedResource[];
  selectSeatFromMap: (resources: SelectedResource[], isOk: boolean, valueCents: number) => void;
}

/**
 * Single source of truth for the seat-map selection state shared by the
 * legacy Product Adder and Product Booking flows. It exposes the same
 * Redux-derived data (product, selected tariffs, selected hour, current
 * scheduling) and the `setSelectedResources` dispatcher both flows already use,
 * so each feature can reuse it without duplicating selectors or changing its
 * own seat-map rules.
 */
export function useProductSeatmapSelection(): UseProductSeatmapSelectionResult {
  const widgetId = useWidgetId();
  const productId = useSelectedProductId();
  const dispatch = useAppDispatch();

  const product = useAppSelector(productSelector(productId ?? ''));
  const tariffsPerTypeSelected = useAppSelector(selectedTariffsSelector(widgetId, productId ?? undefined));
  const selectedHourValue = useAppSelector(selectedProductTariffHour(widgetId, productId ?? undefined));
  const selectedResources = useAppSelector(selectedProductResources(widgetId, productId ?? undefined));

  const selectedHour: ScheduleTime | null = selectedHourValue ?? null;
  const selectedTime = selectedHourValue?.minutes;

  const selectedTariffs = useMemo(
    () => makeSelectedTariffs(tariffsPerTypeSelected, selectedTime ?? null),
    [tariffsPerTypeSelected, selectedTime],
  );

  const scheduling = useMemo(() => {
    if (!product) {
      return undefined;
    }
    return getSchedulingFrom(product, tariffsPerTypeSelected, selectedTime);
  }, [product, tariffsPerTypeSelected, selectedTime]);

  const requiresSeatSelection = Boolean(scheduling?.requiresSeatSelection);

  const selectSeatFromMap = useCallback(
    (resources: SelectedResource[], isOk: boolean, valueCents: number) => {
      dispatch(setSelectedResources(widgetId, resources, isOk, valueCents));
    },
    [dispatch, widgetId],
  );

  return {
    widgetId,
    productId,
    product,
    tariffsPerTypeSelected,
    selectedHour,
    selectedTime,
    selectedTariffs,
    scheduling,
    requiresSeatSelection,
    selectedResources,
    selectSeatFromMap,
  };
}
